import { useRef, useState } from 'react';
import { Alert, Button, Field, Input, useFocusTrap } from '@truecare/ui';
import { ApiError, unwrap } from '@truecare/api-client';
import { api } from '../api';

/**
 * Phase 14a-6 — TOTP step-up modal interceptor.
 *
 * Opened by the global TanStack Query mutation error handler in
 * `lib/totp-step-up-handler.ts` when a mutation rejects with
 * `error.reason === 'totp_step_up_required'`. Captures a 6-digit TOTP
 * code, posts it to /api/v1/auth/totp-step-up, then resolves so the
 * caller can auto-retry the failing mutation.
 *
 * UX rules per 14a-6:
 *   - sequential modal for parallel mutations (one queue)
 *   - failed code keeps the modal open with an error toast; mutation
 *     stays locked until success or operator dismisses
 *   - dismissal cancels the pending mutation
 */
export interface TotpStepUpModalProps {
  open: boolean;
  onSuccess: () => void;
  onCancel: () => void;
}

export function TotpStepUpModal({ open, onSuccess, onCancel }: TotpStepUpModalProps) {
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Focus in on open, trap Tab, Escape-to-cancel, restore focus on close
  // (H-F5). This modal gates EVERY TOTP-fresh destructive admin action,
  // so keyboard/screen-reader operators must be able to work it fully.
  useFocusTrap(panelRef, { active: open, onEscape: onCancel });

  if (!open) return null;

  async function submit(): Promise<void> {
    setPending(true);
    setError(null);
    try {
      await unwrap(
        api.POST('/api/v1/auth/totp-step-up' as never, { body: { token } } as never),
      );
      setToken('');
      onSuccess();
    } catch (err) {
      // 14b-1 post-audit H2 — discriminate 429 (rate-limit hit, 3/min
      // per IP from @Throttle on the backend) from generic 401 so the
      // operator sees the correct failure mode. Before this branch,
      // a 429 was mislabeled as "incorrect code" — confusing UX exactly
      // when the operator is most stressed (mid-destructive-action).
      if (err instanceof ApiError && err.status === 429) {
        setError('Too many attempts. Please wait a moment and try again.');
      } else if (err instanceof ApiError) {
        setError('That code is incorrect or has expired. Try again.');
      } else {
        setError('Step-up failed — please try again.');
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="totp-step-up-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      data-testid="totp-step-up-modal"
    >
      <div className="w-full max-w-md rounded-md border border-border bg-cream p-6">
        <h2 id="totp-step-up-modal-title" className="font-heading text-xl text-forest">
          Re-verify TOTP
        </h2>
        <p className="mt-2 text-sm text-ink-muted">
          This action requires a fresh authenticator code. Enter the current
          6-digit code from your authenticator app.
        </p>
        {error && (
          <Alert tone="danger" className="mt-4">
            {error}
          </Alert>
        )}
        <form
          className="mt-4 flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          noValidate
        >
          <Field label="6-digit code">
            <Input
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={token}
              onChange={(e) => setToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
              data-testid="totp-step-up-code"
            />
          </Field>
          <div className="flex justify-end gap-3">
            <Button variant="ghost" type="button" onClick={onCancel} disabled={pending}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={pending}
              disabled={token.length !== 6}
              data-testid="totp-step-up-submit"
            >
              Confirm
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
